import { useState, useCallback } from "react";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { useMode } from "@/providers/ModeProvider";
import { toast } from "sonner";

interface TransactionDocumentDownloadState {
  isDownloading: boolean;
  downloadTransactionDocument: (transactionId: number) => Promise<void>;
}

/**
 * Hook to view a transaction document via the backend endpoint.
 * Fetches the document as a blob and opens it in a new browser tab.
 * In demo mode the button is a no-op.
 */
export function useTransactionDocumentDownload(): TransactionDocumentDownloadState {
  const { apiBaseUrl, getHeaders } = useApiConfig();
  const { isDemo } = useMode();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadTransactionDocument = useCallback(
    async (transactionId: number) => {
      if (isDemo) {
        // In demo mode, do nothing - avoid opening example URLs
        return;
      }

      setIsDownloading(true);
      try {
        const headers = await getHeaders();
        // Remove Content-Type for blob request - the browser will set it
        const { "Content-Type": _contentType, ...headersWithoutContentType } =
          headers as Record<string, string>;
        void _contentType;

        const response = await fetch(
          `${apiBaseUrl}/api/v1/transactions/${transactionId}/document/download`,
          {
            method: "GET",
            headers: headersWithoutContentType,
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            toast.error("No document available for this transaction");
            return;
          }
          if (response.status === 400) {
            toast.error("Document download is not supported for this transaction type");
            return;
          }
          toast.error("Failed to download document");
          return;
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");
        const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
        const filename = filenameMatch?.[1] ?? `transaction_${transactionId}.pdf`;
        const contentType =
          response.headers.get("Content-Type") ?? "application/pdf";

        const blobUrl = URL.createObjectURL(
          new Blob([blob], { type: contentType })
        );

        // Open in a new tab for viewing
        const newTab = window.open(blobUrl, "_blank");
        if (!newTab) {
          // Fallback: trigger download if popup blocked
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        // Clean up the blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (error) {
        console.error("Document download failed:", error);
        toast.error("Failed to download document");
      } finally {
        setIsDownloading(false);
      }
    },
    [apiBaseUrl, getHeaders, isDemo]
  );

  return { isDownloading, downloadTransactionDocument };
}
